/**
 * What `Checkbox`, `RadioGroup`, `Switch` and `SegmentedControl` share: the native input drawn as
 * the visible mark, and the arrow keys of a group that chooses only on Space or Enter.
 *
 * Each control keeps a real `<input>` and draws it with `appearance: none`, so the browser's own
 * keyboard handling, form behaviour and screen reader roles stay, and the focus ring sits on the
 * element that has focus.
 */
import * as stylex from "@stylexjs/stylex";
import type { KeyboardEvent } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/space.stylex";
import { border } from "@lattice/tokens/space.stylex";
export const choiceStyle = {
  mark: {
    kysU6D: "xjyslct",
    kogj98: "x1ghz6dp",
    kmuXW: "x2lah0s",
    kWkggS: "x40k5rp",
    kMzoRj: "xdq7b4a",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xdmng3o xbqcxop",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x11vqxbs",
    kIyJzY: "x149xavy",
    kAMwcw: "xa02eh3",
    $$css: true
  },
  on: {
    kWkggS: "xmbr0q7",
    kVAM5u: "xtfhg7g x1ihcl1v",
    $$css: true
  },
  invalid: {
    kVAM5u: "xjygxg",
    $$css: true
  },
  disabled: {
    kWkggS: "x14ba300",
    kVAM5u: "x17k0sde",
    kkrTdU: "x1h6gzvc",
    $$css: true
  },
  busy: {
    kkrTdU: "x19v85br",
    $$css: true
  },
  disabledOn: {
    kWkggS: "x1nz7ert",
    kVAM5u: "x6r538p",
    $$css: true
  }
};
const NEXT = new Set(["ArrowDown", "ArrowRight"]);
const PREVIOUS = new Set(["ArrowUp", "ArrowLeft"]);

/**
 * For a group where choosing has an effect at once, such as a question that moves on as soon as it
 * is answered or a choice that saves. The browser's arrow keys check each radio they pass, which
 * would answer the question with the first press. Here they only move focus, wrapping at the ends,
 * and Space or Enter chooses.
 */
export function moveWithoutChoosing(event: KeyboardEvent<HTMLInputElement>, choose: (value: string) => void) {
  const here = event.currentTarget;
  if (event.key === "Enter") {
    event.preventDefault();
    choose(here.value);
    return;
  }
  const step = NEXT.has(event.key) ? 1 : PREVIOUS.has(event.key) ? -1 : 0;
  if (step === 0) return;
  event.preventDefault();
  const radios = [...(here.closest("fieldset")?.querySelectorAll<HTMLInputElement>("input[type=radio]:not(:disabled)") ?? [])];
  const at = radios.indexOf(here);
  radios.at((at + step) % radios.length)?.focus();
}